import React from 'react';
import { Box, Card, CardContent, Typography, Select, MenuItem, Tooltip, IconButton, Button, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { ArrowForward as ArrowForwardIcon, Cloud as CloudIcon } from '@mui/icons-material';

const GridCell = styled(Box)(({ theme, color, empty }) => ({
  width: '100%',
  paddingBottom: '68%',
  backgroundColor: color,
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  cursor: empty ? 'default' : 'pointer',
  transition: 'all 0.2s ease',
  border: empty ? `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}` : 'none',
  '&:hover': {
    opacity: empty ? 1 : 0.9,
    transform: empty ? 'none' : 'scale(1.02)',
  }
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: theme.spacing(1.25),
  width: '100%'
}));

const ServerName = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: theme.typography.pxToRem(12),
  color: theme.palette.text.primary,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '90%',
  fontWeight: 600,
  textShadow: theme.palette.mode === 'dark'
    ? '0 1px 3px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.5)'
    : '0 1px 2px rgba(0, 0, 0, 0.3)',
  WebkitTextStroke: theme.palette.mode === 'dark' ? '0.5px rgba(0, 0, 0, 0.3)' : 'none'
}));

export const metrics = [
  { value: 'status', label: 'Node Status' },
  { value: 'gpuVendor', label: 'GPU Vendors' }
];

// Removed mock server data - now using real API data

const ColorBar = ({ metric }) => {
  const getColorBarLabels = (metric) => {
    if (metric === 'status') {
      return ['Active', 'Idle', 'Inactive'];
    }
    if (metric === 'nodeHealth') {
      return ['Healthy', 'Warning', 'Critical'];
    }
    return ['100%', '70%', '30%', '0%'];
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      width: 50,
      ml: 2
    }}>
      <Typography variant="caption" sx={{ textAlign: 'center', mb: 1 }}>High</Typography>
      <Box sx={{ 
        flex: 1,
        background: 'linear-gradient(to bottom, error.main, warning.main, success.main)',
        borderRadius: 1,
        my: 1,
        height: 200,
        position: 'relative'
      }}>
        {getColorBarLabels(metric).map((label, index, array) => (
          <Typography
            key={label}
            variant="caption"
            sx={{
              position: 'absolute',
              right: -5,
              transform: 'translateX(100%)',
              color: 'text.secondary',
              fontSize: '0.7rem',
              ...(index === 0 ? {
                top: '0%',
              } : index === array.length - 1 ? {
                bottom: '0%',
              } : {
                top: `${(index / (array.length - 1)) * 100}%`,
                transform: 'translateX(100%) translateY(-50%)',
              })
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>
      <Typography variant="caption" sx={{ textAlign: 'center', mt: 1 }}>Low</Typography>
    </Box>
  );
};

// Transform real server data to show status and GPU vendor info
const transformServerData = (server, index) => {
  // Extract real information from the API response
  const hostname = server.nodeMetadata?.system_info?.hostname || server.hostname || `Node ${index + 1}`;
  
  // Get detailed GPU breakdown
  const getGpuBreakdown = (server) => {
    const gpus = server.nodeMetadata?.gpu_info?.driver || server.gpus || [];
    if (gpus.length === 0) return { vendor: 'No GPU', breakdown: 'No GPUs', totalCount: 0 };
    
    // Count GPUs by model
    const gpuCounts = {};
    const vendors = new Set();
    
    gpus.forEach(gpu => {
      // Clean up model name
      let model = gpu.model || 'Unknown GPU';
      if (model.includes('INSTINCT')) {
        model = model.replace('INSTINCT ', '').replace(/\s+/g, '').toUpperCase();
      } else {
        model = model.replace(/instinct_/i, '').replace(/_/g, '').toUpperCase();
      }
      
      // Track vendor
      if (gpu.vendor === 2) vendors.add('AMD');
      else if (gpu.vendor === 1) vendors.add('NVIDIA');
      else vendors.add('Unknown');
      
      // Count by model
      gpuCounts[model] = (gpuCounts[model] || 0) + 1;
    });
    
    // Create breakdown string like "2x MI100, 4x MI210"
    const breakdown = Object.entries(gpuCounts)
      .map(([model, count]) => `${count}x ${model}`)
      .join(', ');
    
    // Determine vendor label
    let vendorLabel;
    if (vendors.size === 1) {
      vendorLabel = `${Array.from(vendors)[0]} (${gpus.length})`;
    } else {
      vendorLabel = `Mixed (${gpus.length})`;
    }
    
    return {
      vendor: vendorLabel,
      breakdown: breakdown,
      totalCount: gpus.length,
      models: Array.from(new Set(Object.keys(gpuCounts)))
    };
  };
  
  // Use the status from transformNodeToDisplayFormat (already processed)
  const displayStatus = server.status || 'Unknown';
  const gpuInfo = getGpuBreakdown(server);
  
  return {
    id: server.id,
    name: hostname,
    status: displayStatus,
    gpuVendor: gpuInfo.vendor,
    // Real hardware specs for tooltip
    realSpecs: {
      status: displayStatus,
      memory: server.nodeMetadata?.memory_info?.total || 'Unknown',
      cpuCores: server.nodeMetadata?.cpu_info?.cores || server.numOfCpus || 0,
      cpuModel: server.nodeMetadata?.cpu_info?.model || 'Unknown',
      gpuCount: server.numOfGpus || gpuInfo.totalCount,
      gpuBreakdown: gpuInfo.breakdown,
      gpuModels: gpuInfo.models,
      storage: `${Math.round(server.storage || 0)}GB`,
      uptime: server.nodeMetadata?.uptime_info?.human || 'Unknown',
      ipAddress: server.addressDetails?.ipAddress || server.nodeMetadata?.network_info?.addresses?.public || 'Unknown'
    }
  };
};

export default function ServerGridDisplay({ selectedMetric, setSelectedMetric, servers = [], hasAccess }) {
  const theme = useTheme();
  // Transform servers data and limit to first 15 for the grid
  const transformedServers = servers.slice(0, 15).map((server, index) => 
    transformServerData(server, index)
  );

  const getConsistentColor = (colorType) => {
    switch (colorType) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'secondary':
        return theme.palette.secondary.main;
      case 'info':
        return theme.palette.info.main;
      case 'gray':
        return theme.palette.grey[500];
      default:
        return theme.palette.grey[500];
    }
  };

  const getColor = (value, metric) => {
    if (value === undefined) return theme.palette.background.paper;
    
    // Color coding based on status from NodeService transformNodeToDisplayFormat
    if (metric === 'status') {
      switch (value) {
        case 'Active': return getConsistentColor('success');              // Consistent green for active
        case 'Registering': return getConsistentColor('warning');         // Consistent orange for registering
        case 'Registration Failed': return getConsistentColor('error');   // Consistent red for registration failed
        case 'Offline': return getConsistentColor('gray');                // Consistent gray for offline
        case 'Maintenance': return getConsistentColor('secondary');       // Consistent purple for maintenance
        default: return getConsistentColor('gray');                        // Default gray
      }
    }
    
    // Color coding for GPU vendors
    if (metric === 'gpuVendor') {
      if (value.includes('AMD')) return getConsistentColor('error');        // Consistent red for AMD
      if (value.includes('NVIDIA')) return getConsistentColor('success');   // Consistent green for NVIDIA
      if (value.includes('Mixed')) return getConsistentColor('secondary');  // Consistent purple for mixed
      if (value.includes('No GPU')) return getConsistentColor('gray');      // Consistent gray for no GPU
      return getConsistentColor('info');                                    // Consistent blue for unknown
    }
    
    return getConsistentColor('info'); // Default to blue
  };

  const getValue = (server, metric) => {
    if (!server) return undefined;
    return server[metric];
  };

  const getTooltipContent = (server, index) => {
    if (!server) return 'Empty slot - Add a new node to monitor';
    const specs = server.realSpecs;
    return (
      <Box sx={{ p: 1.5, minWidth: 250 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{server.name}</Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>Status:</strong> {specs.status}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>IP Address:</strong> {specs.ipAddress}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>CPU:</strong> {specs.cpuCores} cores
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>Memory:</strong> {specs.memory}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>Storage:</strong> {specs.storage}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
          <strong>Uptime:</strong> {specs.uptime}
        </Typography>
        {specs.gpuCount > 0 ? (
          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'success.main' }}>
            <strong>GPUs:</strong> {specs.gpuBreakdown}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            <strong>GPUs:</strong> No GPUs
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      color: 'text.primary',
      borderRadius: 3,
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`
    }}>
      <CardContent sx={{ 
        p: 0,
        '&:last-child': { pb: 0 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Node Overview
            </Typography>
            <IconButton size="small" sx={{ ml: 0.5, color: 'text.secondary' }}>
              <ArrowForwardIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Select
            size="small"
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            sx={{ 
              minWidth: 110, 
              height: 32,
              '& .MuiOutlinedInput-root': { 
                borderRadius: 1.5,
              },
              '& .MuiSelect-select': { 
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
              }
            }}
          >
            {metrics.map((metric) => (
              <MenuItem key={metric.value} value={metric.value}>
                {metric.label}
              </MenuItem>
            ))}
          </Select>
            {hasAccess && hasAccess('cloudManagement') && (
              <Button
                component={Link}
                to="/cloud-management"
                startIcon={<CloudIcon />}
                size="small"
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  color: 'text.primary',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  height: 32,
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                Add Server
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          <GridContainer>
            {Array(15).fill(null).map((_, index) => {
              const server = transformedServers[index] || null;
              const value = getValue(server, selectedMetric);
              return (
                <Tooltip key={index} title={getTooltipContent(server, index)} arrow placement="top">
                  <GridCell 
                    color={server ? getColor(value, selectedMetric) : 'transparent'} 
                    empty={!server}
                  >
                    {server ? (
                      <ServerName>{server.name}</ServerName>
                    ) : (
                      <ServerName sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 400,
                        textShadow: theme.palette.mode === 'dark'
                          ? '0 1px 2px rgba(0, 0, 0, 0.6)'
                          : '0 1px 2px rgba(0, 0, 0, 0.3)',
                        WebkitTextStroke: 'none'
                      }}>
                        Empty
                      </ServerName>
                    )}
                  </GridCell>
                </Tooltip>
              );
            })}
          </GridContainer>
          
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            mt: 2,
            justifyContent: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {selectedMetric === 'status' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('success'),
                    borderRadius: '50%',
                    mr: 0.75 
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Active</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('warning'),
                    borderRadius: '50%',
                    mr: 0.75  
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Registering</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('error'),
                    borderRadius: '50%',
                    mr: 0.75  
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Registration Failed</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('gray'),
                    borderRadius: '50%',
                    mr: 0.75  
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Offline</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('secondary'),
                    borderRadius: '50%',
                    mr: 0.75  
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Maintenance</Typography>
                </Box>
              </>
            )}
            {selectedMetric === 'gpuVendor' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('success'),
                    borderRadius: '50%',
                    mr: 0.75 
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>NVIDIA</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('error'),
                    borderRadius: '50%',
                    mr: 0.75  
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>AMD</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    bgcolor: getConsistentColor('secondary'),
                    borderRadius: '50%',
                    mr: 0.75  
                  }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Mixed</Typography>
                </Box>
              </>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                border: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                borderRadius: '50%',
                mr: 0.75  
              }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Empty</Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
} 